import React, {ComponentProps} from 'react';

import {ROW_SCOPED_UNIT_TYPE_FILTER_HOOKS} from 'notebook/structured-tables/columns/RowScopedUnitTypeFilters';
import {
  assertSingleUnitScoping,
  findEvaluatedCellByGroup,
  RowScopedUnitTypeFilterConfig,
} from 'notebook/tables/search-scoping/rowScopedUnitTypeFilter';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';
import {CenteredSpinner} from 'ui-library/Spinner';
import UnitReferenceCellInput from 'units/UnitReferenceCellInput';

interface RowScopedUnitReferenceCellInputProps extends ComponentProps<typeof UnitReferenceCellInput> {
  rowScopedUnitTypeFilter: RowScopedUnitTypeFilterConfig;
}

/**
 * Wrapper around {@link UnitReferenceCellInput} for a UNIT_REFERENCE column that opts into a row-scoped unit
 * type filter (see {@link RowScopedUnitTypeFilterConfig}). While the restriction is still loading, no units are
 * offered, so a unit that is about to be disallowed can't be picked.
 */
export default function RowScopedUnitReferenceCellInput({
  rowScopedUnitTypeFilter,
  unitTypesFilterPredicate,
  ...unitReferenceCellInputProps
}: RowScopedUnitReferenceCellInputProps): JSX.Element {
  assertSingleUnitScoping({unitTypeId: unitReferenceCellInputProps.unitTypeId, rowScopedUnitTypeFilter});
  const sourceCell = useRowModel((rowModel) =>
    findEvaluatedCellByGroup(rowScopedUnitTypeFilter.sourceColumnGroup, rowModel)
  );
  // A column's filter type never changes, so the same hook is called on every render.
  const useRowScopedUnitTypeFilter = ROW_SCOPED_UNIT_TYPE_FILTER_HOOKS[rowScopedUnitTypeFilter.type];
  const filterResult = useRowScopedUnitTypeFilter({sourceCell});

  if (filterResult.loading) {
    return <CenteredSpinner aria-label="Loading units" />;
  }
  return (
    <UnitReferenceCellInput
      {...unitReferenceCellInputProps}
      unitTypesFilterPredicate={filterResult.predicate ?? unitTypesFilterPredicate}
    />
  );
}
